import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { TrendingUp, CreditCard, AlertCircle, Users } from 'lucide-react';

const DashboardStats = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const uRole = user?.role || user?.Role;
      const uClient = user?.clientId || user?.ClientId;
      const url = uRole === 'SuperAdmin' ? '/api/invoice/stats' : `/api/invoice/stats?clientId=${uClient}`;
      const res = await fetch(url);
      const data = await res.json();
      setStats(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem' }}>Loading analytics...</div>;
  if (!stats) return <div style={{ textAlign: 'center', padding: '2rem' }}>No analytics data available.</div>;

  // Find Paid and Unpaid objects
  // Using case-insensitive find just in case
  const paidItem = stats.statusCounts?.find(d => d.paymentStatus?.toLowerCase() === 'paid') || { statusCountVal: 0, totalAmount: 0 };
  const unpaidItem = stats.statusCounts?.find(d => d.paymentStatus?.toLowerCase() === 'unpaid') || { statusCountVal: 0, totalAmount: 0 };

  // Data for Charts
  const pieData = [
    { name: 'Paid', value: paidItem.statusCountVal },
    { name: 'Unpaid', value: unpaidItem.statusCountVal }
  ].filter(d => d.value > 0);

  const barData = stats.topClients.map(c => ({
    name: c.billToCompany.substring(0, 15),
    value: c.totalBusiness
  }));

  return (
    <div className="animate-fade-in">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem', marginBottom: '2.5rem' }}>
        <StatCard 
            title="Monthly Revenue" 
            value={`₹${(stats.monthlyRevenue || 0).toLocaleString()}`} 
            icon={<TrendingUp size={24} />} 
            color="#4F46E5" 
            subtitle="Current month generated"
        />
        <StatCard 
            title="Top Clients Flow" 
            value={stats.topClients.length} 
            icon={<Users size={24} />} 
            color="#8B5CF6" 
            subtitle="Clients with transactions"
        />
        <StatCard 
            title="Total Collection" 
            value={`₹${(paidItem.totalAmount || 0).toLocaleString()}`} 
            icon={<CreditCard size={24} />} 
            color="#10B981" 
            subtitle={`${paidItem.statusCountVal} Invoices Paid`}
        />
        <StatCard 
            title="Outstanding" 
            value={`₹${(unpaidItem.totalAmount || 0).toLocaleString()}`} 
            icon={<AlertCircle size={24} />} 
            color="#EF4444" 
            subtitle={`${unpaidItem.statusCountVal} Invoices Pending`}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
        <div className="form-section">
          <h3 className="section-title">Invoice Status (By Count)</h3>
          <div style={{ height: '300px', width: '100%' }}>
            {pieData.length > 0 ? (
                <ResponsiveContainer>
                    <PieChart>
                    <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                    >
                        {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.name === 'Paid' ? '#10B981' : '#EF4444'} />
                        ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36}/>
                    </PieChart>
                </ResponsiveContainer>
            ) : <div style={{ textAlign: 'center', paddingTop: '100px', color: '#94a3b8' }}>No invoice status data</div>}
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Top 5 ClientsBusiness</h3>
          <div style={{ height: '300px', width: '100%' }}>
            {barData.length > 0 ? (
                <ResponsiveContainer>
                    <BarChart data={barData}>
                        <XAxis dataKey="name" fontSize={10} />
                        <YAxis fontSize={10} />
                        <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                        <Bar dataKey="value" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            ) : <div style={{ textAlign: 'center', paddingTop: '100px', color: '#94a3b8' }}>No client data available</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <div style={{ 
    background: 'white', 
    padding: '1.5rem', 
    borderRadius: '16px', 
    border: '1px solid var(--border-color)', 
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', alignItems: 'center' }}>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontWeight: '600' }}>{title}</span>
        <div style={{ color: color, background: `${color}15`, padding: '0.5rem', borderRadius: '12px' }}>{icon}</div>
    </div>
    <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.25rem' }}>{value}</div>
    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{subtitle}</div>
    <div style={{ position: 'absolute', bottom: 0, left: 0, width: '4px', height: '100%', background: color }}></div>
  </div>
);

export default DashboardStats;
